import { isActivePerk, perkQuantity } from './perks';
import type { Card, Perk, Program, Redemption } from './schema';
import { diffDays } from './time';
import { PROGRAMS, programInfo } from './valuations';

/** Cents per point a redemption got, after taxes and fees. Null without a cash price. */
export function centsPerPoint(r: Pick<Redemption, 'points' | 'cashValue' | 'fees'>): number | null {
  if (r.cashValue == null || r.points <= 0) return null;
  return ((r.cashValue - (r.fees ?? 0)) / r.points) * 100;
}

/** Dollar value of a balance at a given cents-per-point rate. */
export function pointsValue(balance: number, cpp: number): number {
  return (balance * cpp) / 100;
}

export type SoonKind = 'perk' | 'benefit' | 'fee' | 'points';

export interface SoonEntry {
  kind: SoonKind;
  /** Record to open: a perk, card or program id. */
  id: string;
  title: string;
  detail: string;
  /** Local date it matters by. */
  on: string;
  days: number;
  /** Dollars at stake, when known. */
  value: number | null;
}

/**
 * Everything with a date coming up: perks and certificates expiring, card credits left before
 * they reset, annual fees coming due, and points about to expire. Soonest first.
 */
export function dueSoon(
  wallet: { cards: Card[]; perks: Perk[]; programs: Program[] },
  today: string,
  horizonDays = 60,
): SoonEntry[] {
  const out: SoonEntry[] = [];
  const add = (e: Omit<SoonEntry, 'days'>) => {
    const days = diffDays(today, e.on);
    if (days >= 0 && days <= horizonDays) out.push({ ...e, days });
  };
  for (const p of wallet.perks) {
    if (!isActivePerk(p, today) || !p.expiresOn) continue;
    const left =
      p.progress && p.progress.target > p.progress.done
        ? p.progress.target - p.progress.done
        : null;
    add({
      kind: 'perk',
      id: p.id,
      title: perkQuantity(p) > 1 ? `${p.title} ×${perkQuantity(p)}` : p.title,
      detail:
        left != null ? `$${Math.round(left).toLocaleString('en-US')} more to spend` : 'Expires',
      on: p.expiresOn,
      value: p.unit === 'usd' ? p.value : null,
    });
  }
  for (const c of wallet.cards) {
    if (c.deleted) continue;
    if (c.feeDueOn && c.annualFee > 0)
      add({
        kind: 'fee',
        id: c.id,
        title: `${c.name} annual fee`,
        detail: `$${c.annualFee.toLocaleString('en-US')} due`,
        on: c.feeDueOn,
        value: c.annualFee,
      });
    for (const b of c.benefits) {
      if (!b.resetsOn || b.value == null) continue;
      const left = b.value - (b.used ?? 0);
      if (left <= 0) continue;
      add({
        kind: 'benefit',
        id: c.id,
        title: b.title,
        detail: `$${Math.round(left).toLocaleString('en-US')} left on ${c.name}`,
        on: b.resetsOn,
        value: left,
      });
    }
  }
  for (const p of wallet.programs) {
    if (p.deleted || !p.expiresOn || p.balance <= 0) continue;
    add({
      kind: 'points',
      id: p.id,
      title: `${p.name} points expire`,
      detail: `${p.balance.toLocaleString('en-US')} points`,
      on: p.expiresOn,
      value: null,
    });
  }
  return out.sort((a, b) => a.days - b.days || (b.value ?? 0) - (a.value ?? 0));
}

/** Partner points one bank point becomes, for the transfers people actually make. */
const TRANSFERS: Record<string, Record<string, number>> = {
  amex_mr: {
    aeroplan: 1,
    avios: 1,
    delta: 1,
    flyingblue: 1,
    krisflyer: 1,
    virgin: 1,
    marriott: 1,
    hilton: 2,
    jetblue: 0.8,
    emirates: 0.8,
  },
  chase_ur: {
    united: 1,
    hyatt: 1,
    aeroplan: 1,
    flyingblue: 1,
    krisflyer: 1,
    virgin: 1,
    avios: 1,
    southwest: 1,
    jetblue: 1,
    ihg: 1,
    marriott: 1,
    emirates: 1,
  },
  capital_one: {
    aeroplan: 1,
    flyingblue: 1,
    krisflyer: 1,
    virgin: 1,
    avios: 1,
    turkish: 1,
    emirates: 0.75,
    wyndham: 1,
    choice: 1,
    accor: 0.5,
  },
  citi_ty: {
    american: 1,
    jetblue: 1,
    flyingblue: 1,
    virgin: 1,
    krisflyer: 1,
    turkish: 1,
    avios: 1,
    emirates: 0.8,
    choice: 2,
    wyndham: 1,
  },
  bilt: {
    american: 1,
    united: 1,
    hyatt: 1,
    aeroplan: 1,
    flyingblue: 1,
    virgin: 1,
    avios: 1,
    alaska: 1,
    turkish: 1,
    emirates: 1,
    ihg: 1,
    marriott: 1,
    hilton: 1,
  },
  wells_fargo: { avios: 1, flyingblue: 1, virgin: 1, choice: 2 },
};

/** The program a redemption used; "other" ones are matched by name in the title (JetBlue Mint). */
export function redemptionProgram(r: Pick<Redemption, 'programId' | 'title'>): string {
  if (r.programId !== 'other') return r.programId;
  const t = r.title.toLowerCase();
  const hit = PROGRAMS.find((p) => t.includes((p.name.split(' ')[0] ?? '').toLowerCase()));
  return hit?.id ?? 'other';
}

export interface PointValue {
  /** Cents per point. */
  low: number;
  main: number;
  high: number;
  /** Where the main value comes from: my redemptions, my airline redemptions, or published. */
  basis: 'mine' | 'transfers' | 'published';
  /** Redemptions behind it. */
  count: number;
}

const round = (n: number) => Math.round(n * 100) / 100;

/**
 * What a program's points are worth to me: the main value is what my own redemptions got
 * (weighted by points), or for bank points what they got after transferring to partners I
 * actually redeem with. Low is a cautious floor; high is my best or the best sweet spot.
 */
export function pointValue(programId: string, redemptions: Redemption[]): PointValue | null {
  const info = programInfo(programId);
  if (!info) return null;
  const valued = redemptions
    .filter((r) => !r.deleted)
    .map((r) => ({ program: redemptionProgram(r), points: r.points, cpp: centsPerPoint(r) }))
    .filter(
      (r): r is { program: string; points: number; cpp: number } => r.cpp != null && r.cpp > 0,
    );
  const sweet = info.sweetSpots.reduce((m, x) => Math.max(m, x.cppHigh), info.typicalCpp);

  let samples = valued.filter((r) => r.program === programId);
  let basis: PointValue['basis'] = 'mine';
  if (!samples.length && info.kind === 'bank') {
    // I only use bank points by moving them to airlines, so they're worth what my airline
    // redemptions get, at each program's transfer ratio (1:1 where it isn't a partner).
    const partners = TRANSFERS[programId] ?? {};
    samples = valued
      .filter((r) => programInfo(r.program)?.kind === 'airline')
      .map((r) => ({ ...r, cpp: r.cpp * (partners[r.program] ?? 1) }));
    basis = 'transfers';
  }
  if (!samples.length) {
    return {
      low: round(info.typicalCpp * 0.6),
      main: info.typicalCpp,
      high: sweet,
      basis: 'published',
      count: 0,
    };
  }
  const weighted =
    samples.reduce((n, r) => n + r.cpp * r.points, 0) / samples.reduce((n, r) => n + r.points, 0);
  const best = Math.max(...samples.map((r) => r.cpp));
  const worst = Math.min(...samples.map((r) => r.cpp));
  const high = Math.max(sweet, best);
  return {
    low: round(Math.min(basis === 'transfers' ? info.typicalCpp : info.typicalCpp * 0.6, worst)),
    main: round(weighted),
    high: round(high),
    basis,
    count: samples.length,
  };
}
